import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  Archive,
  Bookmark,
  ChevronRight,
  Hand,
  ListChecks,
  Loader2,
  Plus,
  Search,
  SquareCheck,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { cn } from "@/lib/utils";
import { api } from "../api";
import { type BulkAction, BulkActionBar, useBulkIssueActions } from "../components/issues/bulk-actions";
import { FilterBar, GroupSortControls, useIssueFilters } from "../components/issues/filter-bar";
import { groupIssues } from "../components/issues/group-issues";
import { InsightsDrawer } from "../components/issues/insights-drawer";
import { IssueRow } from "../components/issues/issue-row";
import { useIssuesMeta } from "../components/issues/meta";
import { EmptyState, PageHeader } from "../components/page";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs } from "../components/ui/tabs";
import { Toggle } from "../components/ui/toggle";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import { ViewChipsBar, ViewTab, ViewTabMenu, ViewTabsList, ViewToolbar } from "../components/view-tabs";
import { useDraftQuery } from "../lib/issue-search";
import {
  countQuery,
  DEFAULT_ISSUE_TAB,
  ISSUE_PRESETS,
  ISSUE_TABS,
  type IssueTab,
  issueGrouping,
  issueQuery,
  issueTab,
  presetActive,
  togglePreset,
  waitingScope,
} from "../lib/issue-views";
import { useIsManager, useIsMember } from "../lib/permissions";
import { selectRange, toggleId } from "../lib/selection";
import { useLocalStorage } from "../lib/use-local-storage";
import type { IssueView } from "../types";

const PAGE_SIZE = 100;
const REFRESH_MS = 15_000;

const HUMAN_TAB = ISSUE_TABS.find((entry) => entry.value === "human")!;

export function IssuesPage() {
  const navigate = useNavigate();
  const manager = useIsManager();
  const member = useIsMember();
  const { params, set, replace } = useIssueFilters();
  const metaQuery = useIssuesMeta();
  const meta = metaQuery.data;
  const tabDef = issueTab(params);
  const tab = tabDef.value;
  const scope = waitingScope(params);
  const setTab = (next: IssueTab) => {
    const updated = new URLSearchParams(params);
    if (next === DEFAULT_ISSUE_TAB) updated.delete("tab");
    else updated.set("tab", next);
    updated.delete("scope");
    replace(updated);
  };
  const [search, setSearch] = useDraftQuery(params.get("q") ?? "", (value) => set("q", value || undefined));
  const groupBy = issueGrouping(params, tabDef.groupBy);
  const grouped = groupBy !== "none";

  const queryParams = useMemo(() => issueQuery(tabDef, params, scope), [tabDef, params, scope]);

  // The one badge on the strip. It follows the filter chips and Mine, but not the search box.
  const humanCountQuery = useMemo(() => countQuery(HUMAN_TAB, params, scope), [params, scope]);
  const humanCount = useQuery({
    queryKey: ["issues", "counts", [humanCountQuery]],
    queryFn: () => api.issueCounts([humanCountQuery]),
    refetchInterval: REFRESH_MS,
    placeholderData: (previous) => previous,
  });
  const waiting = humanCount.data?.counts[0] ?? 0;
  const moreTab = tabDef.more ? tabDef : undefined;

  // Pages arrive in group order, so scrolling fills groups in from the top. A
  // refetch reloads every page already fetched, keeping the scrolled list live.
  const query = useInfiniteQuery({
    queryKey: ["issues", queryParams],
    queryFn: ({ pageParam }) =>
      api.issues({ ...queryParams, limit: String(PAGE_SIZE), offset: String(pageParam) }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastOffset) =>
      lastOffset + PAGE_SIZE < lastPage.total ? lastOffset + PAGE_SIZE : undefined,
    refetchInterval: REFRESH_MS,
    placeholderData: (previous) => previous,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isPlaceholderData } = query;

  // Offsets shift when issues move between page fetches, so an issue can appear on two pages.
  const items = useMemo(() => {
    const seen = new Set<string>();
    const out: IssueView[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const issue of page.items) {
        if (seen.has(issue.id)) continue;
        seen.add(issue.id);
        out.push(issue);
      }
    }
    return out;
  }, [query.data]);
  const total = query.data?.pages[0]?.total ?? 0;
  const groups = useMemo(
    () => (grouped ? groupIssues(items, groupBy, meta) : []),
    [grouped, items, groupBy, meta],
  );
  // Full per-group counts from the server, so a header is right before all its rows load.
  const groupTotals = useMemo(
    () => new Map((query.data?.pages[0]?.groups ?? []).map((group) => [group.key || "none", group.count])),
    [query.data],
  );

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || isFetchingNextPage || isPlaceholderData) return;
    // Re-observing after each page fires again while the sentinel is still on
    // screen, so a tall window keeps loading until it is full.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void fetchNextPage();
      },
      { rootMargin: "800px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, isPlaceholderData, fetchNextPage]);

  // Collapsed group keys per grouping, so collapsing "Done" under status leaves priority alone.
  const [collapsedByGroup, setCollapsedByGroup] = useLocalStorage<Record<string, string[]>>(
    "skynet.issues.collapsedGroups",
    {},
  );
  const collapsed = useMemo(() => new Set(collapsedByGroup[groupBy] ?? []), [collapsedByGroup, groupBy]);
  const toggleGroup = useCallback(
    (key: string) =>
      setCollapsedByGroup((prev) => {
        const keys = new Set(prev[groupBy] ?? []);
        if (keys.has(key)) keys.delete(key);
        else keys.add(key);
        return { ...prev, [groupBy]: [...keys] };
      }),
    [groupBy, setCollapsedByGroup],
  );

  // Multi-select is opt-in via the toggle beside the filters: checkboxes only exist
  // while it is on. Ids live in state; the issues themselves are derived from the
  // current page so anything that scrolls out of the view (archived, refiltered)
  // silently drops out of the selection.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const anchorRef = useRef<string | null>(null);
  // Shift-click ranges span visible rows only, never into a collapsed group.
  const displayOrder = useMemo(
    () =>
      grouped
        ? groups.flatMap((group) => (collapsed.has(group.key) ? [] : group.items.map((issue) => issue.id)))
        : items.map((issue) => issue.id),
    [grouped, groups, collapsed, items],
  );
  const selectedIssues = useMemo(
    () => items.filter((issue) => selectedIds.has(issue.id)),
    [items, selectedIds],
  );
  const selectionActive = selectedIssues.length > 0;

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    anchorRef.current = null;
  }, []);
  const setSelectModeAndReset = useCallback(
    (on: boolean) => {
      setSelectMode(on);
      if (!on) clearSelection();
    },
    [clearSelection],
  );
  const toggleSelect = useCallback(
    (issue: IssueView, range: boolean) => {
      setSelectedIds((prev) =>
        range ? selectRange(prev, displayOrder, anchorRef.current, issue.id) : toggleId(prev, issue.id),
      );
      // Shift-click extends from the existing anchor; a plain click moves it.
      if (!range) anchorRef.current = issue.id;
    },
    [displayOrder],
  );

  // A selection belongs to one view: changing tab, filters or search starts fresh.
  // biome-ignore lint/correctness/useExhaustiveDependencies: queryParams is the trigger, not an input
  useEffect(() => {
    clearSelection();
  }, [queryParams, clearSelection]);

  // Escape backs out one step at a time: first the selection, then select mode itself.
  useEffect(() => {
    if (!selectMode) return;
    const onKey = (event: KeyboardEvent) => {
      // Radix popovers and dialogs preventDefault when they consume Escape.
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable=true], [role=dialog]")) return;
      if (selectionActive) clearSelection();
      else setSelectModeAndReset(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectMode, selectionActive, clearSelection, setSelectModeAndReset]);

  const bulk = useBulkIssueActions(meta);
  const runBulk = (action: BulkAction) =>
    bulk.mutate(
      { issues: selectedIssues, action },
      {
        onSuccess: ({ targets, failed }) => {
          if (targets.length === 0) return;
          // Keep only what failed selected so a retry is one click away.
          setSelectedIds(new Set(failed.map((entry) => entry.issue.id)));
          if (failed.length === 0) anchorRef.current = null;
        },
      },
    );

  const renderRows = (rows: IssueView[]) => (
    <ul className="divide-y divide-border/60">
      {rows.map((issue) => (
        <IssueRow
          key={issue.id}
          issue={issue}
          meta={meta}
          selected={selectedIds.has(issue.id)}
          onToggleSelect={selectMode ? toggleSelect : undefined}
          showWaiting={groupBy !== "waitingOn"}
        />
      ))}
    </ul>
  );

  return (
    <>
      <PageHeader
        title="Issues"
        description="Every bug, enhancement and request — the factory's order book."
        actions={
          member && (
            <div className="flex items-center gap-2">
              <InsightsDrawer meta={meta} />
              <Button size="sm" onClick={() => navigate("/issues/new")}>
                <Plus /> New issue
              </Button>
            </div>
          )
        }
      />
      {/*
       * The view runs edge to edge: a toolbar strip (tabs, search, ordering), a chips strip
       * (filters, selection), then the list, each on its own rule with text on the gutter.
       */}
      <div className="flex flex-col">
        <ViewToolbar
          tabs={
            <div className="flex items-end gap-5">
              <Tabs value={moreTab ? "" : tab} onValueChange={(v) => setTab(v as IssueTab)}>
                <ViewTabsList>
                  {ISSUE_TABS.filter((t) => !t.more).map((t) => (
                    <ViewTab key={t.value} value={t.value}>
                      {t.label}
                      {t.value === "human" && waiting > 0 && (
                        <span className="rounded-full bg-amber-500/15 px-1.5 text-[11px] font-semibold text-amber-700 tabular-nums dark:text-amber-300">
                          {waiting}
                        </span>
                      )}
                    </ViewTab>
                  ))}
                </ViewTabsList>
              </Tabs>
              <ViewTabMenu label={moreTab?.label ?? "More"} active={Boolean(moreTab)}>
                <DropdownMenuRadioGroup
                  value={moreTab?.value ?? ""}
                  onValueChange={(v) => setTab(v as IssueTab)}
                >
                  {ISSUE_TABS.filter((t) => t.more).map((t) => (
                    <DropdownMenuRadioItem key={t.value} value={t.value}>
                      {t.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </ViewTabMenu>
            </div>
          }
        >
          <div className="relative w-full sm:w-60">
            {query.isFetching && query.isPlaceholderData ? (
              <Loader2 className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
            ) : (
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
            )}
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, number, or id"
              className="h-7 bg-muted/40 pl-8 text-xs shadow-none"
              aria-label="Search issues"
            />
          </div>
          <ViewsMenu countFor={(next) => countQuery(tabDef, next, scope)} />
          <GroupSortControls defaultGroupBy={tabDef.groupBy} />
        </ViewToolbar>
        <ViewChipsBar>
          <FilterBar meta={meta}>
            {tab === "human" && (
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                spacing={0}
                value={scope}
                onValueChange={(v) => v && set("scope", v === "mine" ? v : undefined)}
                aria-label="Whose issues"
              >
                <ToggleGroupItem value="everyone" className="h-7 px-2.5 text-xs">
                  Everyone
                </ToggleGroupItem>
                <ToggleGroupItem value="mine" className="h-7 px-2.5 text-xs">
                  Mine
                </ToggleGroupItem>
              </ToggleGroup>
            )}
            {manager && (
              <Toggle
                variant="outline"
                size="sm"
                pressed={selectMode}
                onPressedChange={setSelectModeAndReset}
                aria-label="Select issues"
                className="h-7 border-dashed data-[state=on]:border-solid"
              >
                <SquareCheck className="size-3.5" />
                Select
              </Toggle>
            )}
          </FilterBar>
        </ViewChipsBar>

        {query.isLoading ? (
          <div className="-mx-gutter divide-y divide-border/60">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex h-9 items-center gap-3 px-gutter">
                <Skeleton className="size-3.5 rounded-full" />
                <Skeleton className="h-3.5 w-14" />
                <Skeleton className="h-3.5 flex-1" />
                <Skeleton className="h-3.5 w-20" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={tab === "archived" ? Archive : tab === "human" ? Hand : ListChecks}
            title={
              tab === "archived"
                ? "Nothing archived"
                : tab === "done"
                  ? "No done issues"
                  : tab === "human"
                    ? scope === "mine"
                      ? "Nothing is waiting on you"
                      : "Nothing is waiting on a person"
                    : "No issues match"
            }
            description={
              tab === "archived"
                ? "Canceled issues, duplicates, and anything you archive show up here."
                : tab === "done"
                  ? "Issues show up here once their status is Done."
                  : tab === "human"
                    ? "Sign-offs, held work orders, agent questions, draft pull requests, and stalled issues show up here."
                    : member
                      ? "Change the filters, or press C to create an issue."
                      : "Change the filters to see more issues."
            }
            className="mt-6"
          />
        ) : (
          // Flat list across the full width: group headings stick under the app header,
          // rows sit on hairlines, and every row's text starts on the gutter.
          <div className="-mx-gutter">
            {!grouped && renderRows(items)}
            {groups.map((group, index) => {
              const open = !collapsed.has(group.key);
              return (
                <Fragment key={group.key}>
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => toggleGroup(group.key)}
                    className={cn(
                      "sticky top-12 z-[1] flex h-9 w-full items-center gap-2 border-b bg-background px-gutter text-left text-xs font-semibold transition-colors duration-(--motion-dur) hover:text-foreground",
                      index > 0 && "mt-3",
                    )}
                  >
                    <ChevronRight
                      className={cn(
                        "size-3.5 text-muted-foreground transition-transform duration-(--motion-dur)",
                        open && "rotate-90",
                      )}
                    />
                    {group.icon}
                    {group.label}
                    <span className="font-medium text-muted-foreground tabular-nums">
                      {groupTotals.get(group.key) ?? group.items.length}
                    </span>
                  </button>
                  {open && renderRows(group.items)}
                </Fragment>
              );
            })}
            <div
              ref={sentinelRef}
              className="flex min-h-9 items-center justify-between gap-2 px-gutter text-xs text-muted-foreground"
            >
              <span className="tabular-nums">
                {items.length >= total ? `${items.length} issues` : `${items.length} of ${total} issues`}
              </span>
              {isFetchingNextPage ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="size-3.5 animate-spin" /> Loading more
                </span>
              ) : hasNextPage ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => void fetchNextPage()}
                >
                  Load more
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {/* Keeps the last rows reachable above the floating toolbar. */}
      {selectionActive && <div aria-hidden className="h-14 shrink-0" />}
      <BulkActionBar
        issues={selectedIssues}
        total={items.length}
        meta={meta}
        pending={bulk.isPending}
        onAction={runBulk}
        onSelectAll={() => setSelectedIds(new Set(items.map((issue) => issue.id)))}
        onClear={clearSelection}
      />
    </>
  );
}

/**
 * Occasional views. Picking one adds its filter chips to the current tab, where they can
 * be edited or cleared like any other. Counts load only while the menu is open.
 */
function ViewsMenu({ countFor }: { countFor: (params: URLSearchParams) => Record<string, string> }) {
  const { params, replace } = useIssueFilters();
  const [open, setOpen] = useState(false);
  const views = ISSUE_PRESETS.map((preset) =>
    countFor(presetActive(preset, params) ? params : togglePreset(preset, params)),
  );
  const counts = useQuery({
    queryKey: ["issues", "counts", views],
    queryFn: () => api.issueCounts(views),
    enabled: open,
  });
  const applied = ISSUE_PRESETS.filter((preset) => presetActive(preset, params)).length;
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 text-xs">
          <Bookmark className="size-3.5" />
          Views
          {applied > 0 && <span className="text-muted-foreground tabular-nums">{applied}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Add to this tab
        </DropdownMenuLabel>
        {ISSUE_PRESETS.map((preset, index) => (
          <DropdownMenuCheckboxItem
            key={preset.id}
            checked={presetActive(preset, params)}
            onCheckedChange={() => replace(togglePreset(preset, params))}
          >
            {preset.label}
            <span className="ml-auto text-xs text-muted-foreground tabular-nums">
              {counts.data?.counts[index] ?? ""}
            </span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
